import { useEffect, useMemo, useState } from "react";
import api, { formatAxiosError } from "@/lib/api";
import { useDialog } from "@/context/DialogContext";
import {
  FloppyDisk, PaperPlaneTilt, ArrowClockwise, Eye, EnvelopeSimple,
  CheckCircle, XCircle, Plug, Article, Lightning,
} from "@phosphor-icons/react";

// ----- small local UI helpers -----
function Field({ label, hint, children }) {
  return (
    <div>
      <label className="eyebrow block mb-1">{label}</label>
      {children}
      {hint && <div className="text-[10px] font-mono-mini text-muted-foreground mt-1">{hint}</div>}
    </div>
  );
}

function TextInput({ value, onChange, placeholder, type = "text", testid, disabled }) {
  return (
    <input
      type={type}
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      disabled={disabled}
      className="w-full border border-border px-3 py-2 text-sm disabled:bg-muted disabled:opacity-60"
      data-testid={testid}
    />
  );
}

function Toggle({ checked, onChange, label, testid, disabled }) {
  return (
    <button
      type="button"
      onClick={() => !disabled && onChange(!checked)}
      disabled={disabled}
      className={`inline-flex items-center gap-2 text-sm select-none ${disabled ? "opacity-60 cursor-not-allowed" : "hover:opacity-90"}`}
      data-testid={testid}
    >
      <span
        className={`inline-flex items-center h-5 w-9 rounded-full transition-colors ${checked ? "bg-[#002FA7]" : "bg-gray-300"}`}
      >
        <span className={`inline-block h-4 w-4 bg-white rounded-full transform transition-transform ${checked ? "translate-x-4" : "translate-x-0.5"}`} />
      </span>
      {label && <span>{label}</span>}
    </button>
  );
}

function Flash({ msg }) {
  if (!msg?.text) return null;
  return (
    <div className={`mb-4 px-4 py-2 text-sm border flex items-center gap-2 ${
      msg.type === "ok"
        ? "border-[#22C55E] bg-green-50 text-[#166534]"
        : "border-[#FF3B30] bg-red-50 text-[#FF3B30]"
    }`}>
      {msg.type === "ok" ? <CheckCircle size={14} weight="fill" /> : <XCircle size={14} weight="fill" />}
      {msg.text}
    </div>
  );
}

// ============ SMTP CONFIG CARD ============
function SmtpConfigCard({ config, setConfig, initial, reload }) {
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState({ type: "", text: "" });
  const [testOpen, setTestOpen] = useState(false);
  const [testTo, setTestTo] = useState("");
  const [testing, setTesting] = useState(false);
  const [showPw, setShowPw] = useState(false);

  const dirtyKeys = ["enabled", "from_email", "from_name", "smtp_host", "smtp_port",
    "smtp_user", "smtp_password", "smtp_tls", "app_base_url"];
  const dirty = useMemo(
    () => initial && dirtyKeys.some(k => (config?.[k] ?? "") !== (initial?.[k] ?? "")),
    [config, initial]
  );

  const flash = (type, text) => {
    setMsg({ type, text });
    setTimeout(() => setMsg({ type: "", text: "" }), 4000);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        enabled: !!config.enabled,
        from_email: (config.from_email || "").trim(),
        from_name: (config.from_name || "").trim(),
        smtp_host: (config.smtp_host || "").trim(),
        smtp_port: Number(config.smtp_port) || 587,
        smtp_user: (config.smtp_user || "").trim(),
        smtp_tls: config.smtp_tls || "starttls",
        app_base_url: (config.app_base_url || "").trim(),
      };
      // Only send password if user changed it away from the mask
      if (config.smtp_password !== "***" && config.smtp_password !== initial?.smtp_password) {
        payload.smtp_password = config.smtp_password || "";
      }
      await api.put("/settings/email", payload);
      await reload();
      flash("ok", "Email settings saved.");
    } catch (e) {
      flash("error", formatAxiosError(e));
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async () => {
    if (!testTo.trim() || !testTo.includes("@")) {
      flash("error", "Enter a valid recipient email.");
      return;
    }
    setTesting(true);
    try {
      const r = await api.post("/settings/email/test", { to: testTo.trim() });
      flash("ok", `Test email sent to ${r.data.sent_to}.`);
      setTestOpen(false);
      setTestTo("");
    } catch (e) {
      flash("error", formatAxiosError(e));
    } finally {
      setTesting(false);
    }
  };

  return (
    <section className="border border-border p-6 mb-8">
      <div className="flex items-start justify-between mb-4 flex-wrap gap-3">
        <div>
          <div className="eyebrow flex items-center gap-2 mb-1">
            <Plug size={12} weight="bold" /> SMTP configuration
          </div>
          <h2 className="font-display text-2xl font-black tracking-tight">Outgoing mail</h2>
          <p className="text-sm text-muted-foreground mt-1 max-w-xl">
            Set up your organization&apos;s SMTP server. When the master switch is off, no email is sent
            regardless of individual template settings.
          </p>
        </div>
        <Toggle
          checked={!!config.enabled}
          onChange={(v) => setConfig({ ...config, enabled: v })}
          label={config.enabled ? "Email ENABLED" : "Email DISABLED"}
          testid="email-master-toggle"
        />
      </div>

      <Flash msg={msg} />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <Field label="From email" hint="e.g. noreply@yourdomain.com">
          <TextInput
            value={config.from_email}
            onChange={(v) => setConfig({ ...config, from_email: v })}
            placeholder="noreply@yourdomain.com"
            testid="from-email"
          />
        </Field>
        <Field label="From name" hint="Shown as the sender name in the recipient's inbox">
          <TextInput
            value={config.from_name}
            onChange={(v) => setConfig({ ...config, from_name: v })}
            placeholder="Velacin Notifications"
            testid="from-name"
          />
        </Field>

        <Field label="SMTP host" hint="e.g. smtp.gmail.com, smtp.sendgrid.net, smtp-mail.outlook.com">
          <TextInput
            value={config.smtp_host}
            onChange={(v) => setConfig({ ...config, smtp_host: v })}
            placeholder="smtp.example.com"
            testid="smtp-host"
          />
        </Field>
        <Field label="SMTP port" hint="Common: 587 (STARTTLS), 465 (SSL), 25 (plain)">
          <TextInput
            type="number"
            value={config.smtp_port}
            onChange={(v) => setConfig({ ...config, smtp_port: v })}
            placeholder="587"
            testid="smtp-port"
          />
        </Field>

        <Field label="SMTP username" hint="Usually your email address or an API key ID">
          <TextInput
            value={config.smtp_user}
            onChange={(v) => setConfig({ ...config, smtp_user: v })}
            placeholder="apikey"
            testid="smtp-user"
          />
        </Field>
        <Field label="SMTP password" hint={initial?.smtp_password ? "Leave as *** to keep the existing password" : "Your password or app-specific token"}>
          <div className="flex items-center gap-2">
            <TextInput
              type={showPw ? "text" : "password"}
              value={config.smtp_password}
              onChange={(v) => setConfig({ ...config, smtp_password: v })}
              placeholder="••••••••"
              testid="smtp-password"
            />
            <button
              type="button"
              onClick={() => setShowPw(s => !s)}
              className="border border-border p-2 hover:bg-muted"
              title={showPw ? "Hide" : "Show"}
            >
              <Eye size={14} weight="bold" />
            </button>
          </div>
        </Field>

        <Field label="Encryption" hint="Match your SMTP provider's requirement">
          <div className="flex items-center gap-3" data-testid="smtp-tls-group">
            {["none", "starttls", "ssl"].map(m => (
              <label key={m} className={`flex items-center gap-1.5 text-sm cursor-pointer px-3 py-1.5 border ${config.smtp_tls === m ? "border-[#002FA7] bg-[#002FA7]/5 text-[#002FA7]" : "border-border hover:bg-muted"}`}>
                <input
                  type="radio"
                  name="smtp_tls"
                  checked={config.smtp_tls === m}
                  onChange={() => setConfig({ ...config, smtp_tls: m })}
                  className="accent-[#002FA7]"
                  data-testid={`smtp-tls-${m}`}
                />
                <span className="uppercase text-[10px] font-mono-mini">{m}</span>
              </label>
            ))}
          </div>
        </Field>
        <Field label="App base URL" hint="Used for the {{action_url}} in template links, e.g. https://hr.acme.com">
          <TextInput
            value={config.app_base_url}
            onChange={(v) => setConfig({ ...config, app_base_url: v })}
            placeholder="https://hr.acme.com"
            testid="app-base-url"
          />
        </Field>
      </div>

      <div className="flex items-center gap-2 justify-end pt-4 border-t border-border">
        <button
          onClick={() => setTestOpen(true)}
          className="border border-border px-3 py-2 text-sm hover:bg-muted flex items-center gap-2"
          data-testid="open-test-email"
        >
          <PaperPlaneTilt size={14} weight="bold" /> Send test email
        </button>
        <button
          onClick={save}
          disabled={!dirty || saving}
          className="btn-primary flex items-center gap-2 disabled:opacity-40"
          data-testid="save-email-config"
        >
          <FloppyDisk size={14} weight="bold" /> {saving ? "Saving…" : "Save settings"}
        </button>
      </div>

      {testOpen && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setTestOpen(false)}>
          <div className="bg-white border border-border w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
            <div className="eyebrow flex items-center gap-2 mb-2">
              <PaperPlaneTilt size={12} weight="bold" /> Send test email
            </div>
            <h3 className="font-display text-xl font-black mb-4">Verify SMTP</h3>
            <p className="text-sm text-muted-foreground mb-4">
              This sends a real email using the CURRENT saved SMTP settings (even if the master switch is off).
              Save your settings first if you just changed them.
            </p>
            <Field label="Recipient" hint="Your own email is fine">
              <TextInput
                value={testTo}
                onChange={setTestTo}
                placeholder="you@yourdomain.com"
                testid="test-email-to"
              />
            </Field>
            <div className="flex items-center justify-end gap-2 mt-4">
              <button onClick={() => setTestOpen(false)} className="border border-border px-3 py-2 text-sm hover:bg-muted">Cancel</button>
              <button
                onClick={sendTest}
                disabled={testing}
                className="btn-primary flex items-center gap-2 disabled:opacity-40"
                data-testid="send-test-email"
              >
                <PaperPlaneTilt size={14} weight="bold" /> {testing ? "Sending…" : "Send"}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

// ============ TEMPLATE EDITOR ============
function TemplateEditor({ typeMeta, template, onSaved, onReset }) {
  const { confirm } = useDialog();
  const [draft, setDraft] = useState(template);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState({ type: "", text: "" });
  const [preview, setPreview] = useState(null); // { subject, body_html } | null
  const [previewing, setPreviewing] = useState(false);

  useEffect(() => { setDraft(template); }, [template, typeMeta?.key]);

  const dirty = draft && template && (
    draft.enabled !== template.enabled ||
    draft.subject !== template.subject ||
    draft.body_html !== template.body_html
  );

  const flash = (type, text) => {
    setMsg({ type, text });
    setTimeout(() => setMsg({ type: "", text: "" }), 4000);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/settings/email/templates/${typeMeta.key}`, {
        enabled: !!draft.enabled,
        subject: draft.subject || "",
        body_html: draft.body_html || "",
      });
      await onSaved();
      flash("ok", "Template saved.");
    } catch (e) {
      flash("error", formatAxiosError(e));
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    const ok = await confirm({
      title: `Reset "${typeMeta.label}" template?`,
      description: "Your customizations will be lost and the template will revert to the built-in defaults.",
      confirmText: "Reset template",
      variant: "destructive",
    });
    if (!ok) return;
    setSaving(true);
    try {
      await api.post(`/settings/email/templates/${typeMeta.key}/reset`);
      await onReset();
      flash("ok", "Template reset to defaults.");
    } catch (e) {
      flash("error", formatAxiosError(e));
    } finally {
      setSaving(false);
    }
  };

  const doPreview = async () => {
    setPreviewing(true);
    try {
      // Save-in-memory first? No — preview uses the SAVED template. Warn if dirty.
      if (dirty) {
        const go = await confirm({
          title: "Preview the saved version?",
          description: "Preview uses the SAVED template. Your unsaved changes will not be shown. Save first, or preview the saved version?",
          confirmText: "Preview saved",
          cancelText: "Cancel",
        });
        if (!go) { setPreviewing(false); return; }
      }
      const r = await api.post(`/settings/email/preview?type_key=${encodeURIComponent(typeMeta.key)}`);
      setPreview(r.data);
    } catch (e) {
      flash("error", formatAxiosError(e));
    } finally {
      setPreviewing(false);
    }
  };

  if (!typeMeta || !draft) return null;

  return (
    <div className="border border-border p-6" data-testid={`template-editor-${typeMeta.key}`}>
      <Flash msg={msg} />

      <div className="flex items-start justify-between mb-4 flex-wrap gap-3">
        <div>
          <div className="eyebrow flex items-center gap-2 mb-1">
            <Article size={12} weight="bold" /> Template
          </div>
          <h3 className="font-display text-2xl font-black tracking-tight">{typeMeta.label}</h3>
          <code className="text-[10px] font-mono-mini text-muted-foreground">{typeMeta.key}</code>
        </div>
        <Toggle
          checked={!!draft.enabled}
          onChange={(v) => setDraft({ ...draft, enabled: v })}
          label={draft.enabled ? "Template ENABLED" : "Template DISABLED"}
          testid={`tmpl-enabled-${typeMeta.key}`}
        />
      </div>

      <div className="mb-4">
        <Field label="Subject" hint="Supports {{variables}} — see the cheatsheet below">
          <TextInput
            value={draft.subject}
            onChange={(v) => setDraft({ ...draft, subject: v })}
            placeholder="[{{app_name}}] Something happened"
            testid={`tmpl-subject-${typeMeta.key}`}
          />
        </Field>
      </div>

      <div className="mb-4">
        <Field label="HTML body" hint="Full HTML supported. Use inline styles for the widest email-client compatibility.">
          <textarea
            value={draft.body_html || ""}
            onChange={(e) => setDraft({ ...draft, body_html: e.target.value })}
            rows={16}
            className="w-full border border-border px-3 py-2 text-xs font-mono"
            spellCheck={false}
            data-testid={`tmpl-body-${typeMeta.key}`}
          />
        </Field>
      </div>

      {typeMeta.variables?.length > 0 && (
        <div className="mb-4">
          <div className="eyebrow mb-2 flex items-center gap-2">
            <Lightning size={12} weight="bold" /> Available variables
          </div>
          <div className="flex flex-wrap gap-1.5">
            {typeMeta.variables.map(v => (
              <button
                key={v}
                type="button"
                onClick={() => {
                  const ins = `{{${v}}}`;
                  navigator.clipboard?.writeText(ins).catch(() => {});
                  flash("ok", `Copied ${ins} to clipboard.`);
                }}
                className="text-[10px] font-mono-mini px-2 py-1 border border-border hover:bg-muted"
                title="Click to copy"
              >
                {`{{${v}}}`}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="flex items-center justify-end gap-2 pt-4 border-t border-border">
        <button
          onClick={reset}
          disabled={saving}
          className="border border-border px-3 py-2 text-sm hover:bg-muted flex items-center gap-2 disabled:opacity-40"
          data-testid={`tmpl-reset-${typeMeta.key}`}
        >
          <ArrowClockwise size={14} weight="bold" /> Reset to default
        </button>
        <button
          onClick={doPreview}
          disabled={previewing}
          className="border border-border px-3 py-2 text-sm hover:bg-muted flex items-center gap-2 disabled:opacity-40"
          data-testid={`tmpl-preview-${typeMeta.key}`}
        >
          <Eye size={14} weight="bold" /> {previewing ? "Loading…" : "Preview"}
        </button>
        <button
          onClick={save}
          disabled={!dirty || saving}
          className="btn-primary flex items-center gap-2 disabled:opacity-40"
          data-testid={`tmpl-save-${typeMeta.key}`}
        >
          <FloppyDisk size={14} weight="bold" /> {saving ? "Saving…" : "Save template"}
        </button>
      </div>

      {preview && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setPreview(null)}>
          <div className="bg-white border border-border w-full max-w-3xl max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="p-4 border-b border-border flex items-center justify-between">
              <div>
                <div className="eyebrow flex items-center gap-2"><Eye size={12} weight="bold" /> Preview</div>
                <div className="font-semibold mt-1">{preview.subject}</div>
              </div>
              <button onClick={() => setPreview(null)} className="border border-border px-3 py-1.5 text-sm hover:bg-muted">Close</button>
            </div>
            <iframe
              title="email preview"
              srcDoc={preview.body_html}
              className="flex-1 w-full min-h-[480px] bg-white"
              sandbox=""
              data-testid={`tmpl-preview-frame-${typeMeta.key}`}
            />
          </div>
        </div>
      )}
    </div>
  );
}

// ============ TEMPLATES SECTION (list + editor) ============
function TemplatesSection({ cfg, reload }) {
  const [selected, setSelected] = useState(null); // type key
  const types = cfg.types || [];

  useEffect(() => {
    if (!selected && types.length) setSelected(types[0].key);
  }, [types, selected]);

  const selectedMeta = types.find(t => t.key === selected);
  const selectedTemplate = (cfg.templates || {})[selected];

  return (
    <section className="border border-border p-6">
      <div className="mb-6">
        <div className="eyebrow flex items-center gap-2 mb-1">
          <EnvelopeSimple size={12} weight="bold" /> Notification templates
        </div>
        <h2 className="font-display text-2xl font-black tracking-tight">What we send, when</h2>
        <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
          Each event type below has its own template. Toggle it off to suppress emails of that type without
          disabling the whole system. Click a row to edit its subject and HTML body.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* LEFT: list */}
        <div className="border border-border divide-y divide-border lg:col-span-1 max-h-[560px] overflow-y-auto" data-testid="template-list">
          {types.map(t => {
            const tmpl = (cfg.templates || {})[t.key];
            const isActive = selected === t.key;
            return (
              <button
                key={t.key}
                onClick={() => setSelected(t.key)}
                className={`w-full text-left px-4 py-3 hover:bg-muted transition-colors ${isActive ? "bg-[#002FA7]/5 border-l-4 border-l-[#002FA7]" : "border-l-4 border-l-transparent"}`}
                data-testid={`template-row-${t.key}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-semibold">{t.label}</span>
                  {tmpl?.enabled
                    ? <span className="text-[9px] font-mono-mini px-1.5 py-0.5 bg-green-100 text-green-800">ON</span>
                    : <span className="text-[9px] font-mono-mini px-1.5 py-0.5 bg-gray-100 text-gray-600">OFF</span>}
                </div>
                <div className="text-[10px] font-mono-mini text-muted-foreground mt-0.5">{t.key}</div>
              </button>
            );
          })}
        </div>

        {/* RIGHT: editor */}
        <div className="lg:col-span-2">
          {selectedMeta && selectedTemplate && (
            <TemplateEditor
              typeMeta={selectedMeta}
              template={selectedTemplate}
              onSaved={reload}
              onReset={reload}
            />
          )}
        </div>
      </div>
    </section>
  );
}

// ============ MAIN EMAIL TAB ============
export default function EmailTab() {
  const [cfg, setCfg] = useState(null);
  const [initial, setInitial] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const load = async () => {
    try {
      const r = await api.get("/settings/email");
      setCfg(r.data);
      setInitial(JSON.parse(JSON.stringify(r.data)));
      setErr("");
    } catch (e) {
      setErr(formatAxiosError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  if (loading) return <div className="p-8 text-sm text-muted-foreground">Loading email settings…</div>;
  if (err) return <div className="p-8 text-sm text-[#FF3B30]">{err}</div>;
  if (!cfg) return null;

  return (
    <div>
      <SmtpConfigCard
        config={cfg}
        setConfig={(next) => setCfg({ ...cfg, ...next })}
        initial={initial}
        reload={load}
      />
      <TemplatesSection cfg={cfg} reload={load} />
    </div>
  );
}
